import React, { FunctionComponent, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { noop } from 'lodash-es'
import styled, { css, useTheme } from 'styled-components/native'

import {
  getWellnessCheckInMetadata,
  HowAreYouFeelingTodayOption,
  useWellnessCheckInRulesEngine,
} from '@lyrahealth-inc/shared-app-logic'

import { BodyText, PrimaryButton } from '../../atoms'
import { Subhead, Size as SubheadSize } from '../../atoms/subhead/Subhead'
import { IS_WEB, Layout } from '../../constants'
import { MoodEmojiDisplay } from '../../molecules'
import { ConfidentialityNotice } from '../../molecules/confidentialityNotice/ConfidentialityNotice'
import { WellnessCheckInBackgroundContainer } from '../../templates'
import { ThemeType, tID } from '../../utils'
import { FormBody, FormButtonParams, FormChangeProperties } from '../formBody/FormBody'

export interface WellnessCheckInFormProps {
  currentPage: number
  totalPages: number
  socialCareEnabled: boolean
  onSubmit: (values: object) => void
  onCTAPress: (values: object) => void
  currValues?: object
  setCurrValues: (values: object) => void
  initialMoodOption?: HowAreYouFeelingTodayOption
}

const STICKY_HEADER_MARGIN_TOP_OFFSET = 72
const QUESTIONNAIRE_PAGE_WIDTH = 504
const HOW_ARE_YOU_FEELING_TODAY_PAGE_WIDTH = 576

const HowAreYouFeelingTodayContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  alignItems: 'center',
  paddingHorizontal: theme.spacing['16px'],
  ...(IS_WEB && {
    marginTop: `${80 + STICKY_HEADER_MARGIN_TOP_OFFSET}px`,
  }),
}))

const InstructionsContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  paddingHorizontal: theme.spacing['16px'],
  maxWidth: `${QUESTIONNAIRE_PAGE_WIDTH}px`,
  alignSelf: 'center',
  width: '100%',
  ...(IS_WEB && {
    marginTop: `${48 + STICKY_HEADER_MARGIN_TOP_OFFSET}px`,
  }),
}))

const SocialCareMarginContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.breakpoints.isMobileSized
    ? `${32 + STICKY_HEADER_MARGIN_TOP_OFFSET}px`
    : `${72 + STICKY_HEADER_MARGIN_TOP_OFFSET}px`,
}))

const CTAContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  ...(theme.breakpoints.isMinWidthTablet
    ? { flexDirection: 'row', justifyContent: 'space-between' }
    : { flexDirection: 'column-reverse' }),
}))

/**
 * A component that renders the Wellness Check In Form.
 */

export const WellnessCheckInForm: FunctionComponent<WellnessCheckInFormProps> = ({
  currentPage,
  totalPages,
  socialCareEnabled,
  onSubmit,
  onCTAPress,
  currValues,
  setCurrValues,
  initialMoodOption = HowAreYouFeelingTodayOption.Ok,
}) => {
  const intl = useIntl()
  const {
    breakpoints: { isMobileSized },
    spacing,
  } = useTheme() as ThemeType
  const { schema, uiSchema } = useMemo(
    () => getWellnessCheckInMetadata(isMobileSized, socialCareEnabled),
    [isMobileSized, socialCareEnabled],
  )
  const { runRulesEngine } = useWellnessCheckInRulesEngine()
  const submitRef = useRef(noop)
  const [currMoodOption, setCurrMoodOption] = useState(initialMoodOption)
  const [showPopInEmoji, setShowPopInEmoji] = useState(false)
  const isHowAreYouFeelingTodayPage = currentPage === 1
  const isSocialCarePage = socialCareEnabled && currentPage === totalPages

  const handleFormChange = ({ values }: FormChangeProperties) => {
    if (isHowAreYouFeelingTodayPage) {
      setCurrMoodOption(
        Object.values(HowAreYouFeelingTodayOption)[
          values.howAreYouFeelingToday as number
        ] as HowAreYouFeelingTodayOption,
      )
      setShowPopInEmoji(true)
    }
    setCurrValues(values)
  }

  const handleSaveForm = async ({ values }: { values: object }) => {
    const formResponse = await runRulesEngine({ values })
    onSubmit(formResponse)
  }

  const handleCTAPress = () => {
    if (currentPage === totalPages) {
      submitRef.current()
    }
    if (currValues) {
      onCTAPress(currValues)
    }
  }

  return (
    <WellnessCheckInBackgroundContainer>
      {isHowAreYouFeelingTodayPage ? (
        <HowAreYouFeelingTodayContainer>
          <Subhead
            text={
              <FormattedMessage
                defaultMessage='To start, how are you feeling today?'
                description='Wellness Check In: Initial question for user completing wellness check in form'
              />
            }
            size={isMobileSized ? SubheadSize.MEDIUM : SubheadSize.LARGE}
            textAlign='center'
          />
          <MoodEmojiDisplay
            currentMoodOption={currMoodOption}
            showPopInEmoji={showPopInEmoji}
            onAnimationFinished={() => setShowPopInEmoji(false)}
            labelSize={SubheadSize.XSMALL}
            animationContainerStyle={{ marginTop: spacing['48px'] }}
          />
        </HowAreYouFeelingTodayContainer>
      ) : isSocialCarePage ? (
        <SocialCareMarginContainer />
      ) : (
        <InstructionsContainer>
          <BodyText
            text={
              <FormattedMessage
                defaultMessage='On most days over the past two weeks:'
                description='Context for the wellness check in form for user to answer questions about their mental health for the past two weeks'
              />
            }
          />
        </InstructionsContainer>
      )}
      <FormBody
        name='WellnessCheckInForm'
        schema={schema}
        uiSchema={uiSchema}
        intl={intl}
        saveForm={handleSaveForm}
        onFormChange={handleFormChange}
        showCurrentPageOnly
        currentPage={currentPage}
        handleSubmitRef={submitRef}
        totalPages={totalPages}
        enableTranslations
        initialValues={currValues as Dict}
        formButton={(props: FormButtonParams) => {
          submitRef.current = props.handleSubmit
          return (
            <CTAContainer>
              <ConfidentialityNotice
                {...(isMobileSized && { customStyle: { marginTop: spacing['32px'], marginBottom: spacing['0px'] } })}
              />
              <PrimaryButton
                {...props}
                text={<FormattedMessage defaultMessage='Continue' description='Button to continue to next screen' />}
                onPress={handleCTAPress}
                fullWidth={isMobileSized}
                testID={tID('WellnessCheckInForm-CTAButton')}
              />
            </CTAContainer>
          )
        }}
        scrollContainerCustomStyles={{
          formBodyPageContainer: css`
            background-color: transparent;
            max-width: ${isHowAreYouFeelingTodayPage
              ? HOW_ARE_YOU_FEELING_TODAY_PAGE_WIDTH
              : QUESTIONNAIRE_PAGE_WIDTH}px;
            padding-top: ${isHowAreYouFeelingTodayPage || isMobileSized ? spacing['24px'] : spacing['32px']};
          `,
          scrollContainerStyle: { overflow: 'visible' },
        }}
        formBodyCustomStyles={{
          submitButtonContainer: css`
            border-top-width: 0;
            background: transparent;
            box-shadow: none;
          `,
          submitButtonWrapper: css`
            max-width: ${isHowAreYouFeelingTodayPage
              ? HOW_ARE_YOU_FEELING_TODAY_PAGE_WIDTH
              : QUESTIONNAIRE_PAGE_WIDTH}px;
            padding-left: ${isMobileSized ? 0 : 16}px;
            padding-right: ${isMobileSized ? 0 : 16}px;
          `,
        }}
        {...(!IS_WEB && {
          scrollListHeight: isHowAreYouFeelingTodayPage ? Layout.window.height - 480 : Layout.window.height - 280,
        })}
      />
    </WellnessCheckInBackgroundContainer>
  )
}
